/**
 * /superadmin/orbitprint/orgs/:orgId — ONE home for everything about an organization
 * (docs/PLATFORM-ADMIN-ARCHITECTURE.md §3): header · plan & money · locations & HUBs · members ·
 * usage · recent jobs · history. Slice 3b: the actions — set plan (modal with the plan table),
 * extend (popover with presets), suspend (reason, destructive) / activate / deactivate, notes
 * (save on blur), "Open as owner" (support session in a new tab) + copy link.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import PlatformAdminShell, { HOUSE, Badge, RouteState, Modal, ActionButton, inputStyle, card, sectionTitle, useSuperAdminGate } from '../PlatformAdminShell';
import { orbitAdminApi, SHELVES, ACTION_LABELS, PLAN_CONFIG, dateInEffect, fmtDate, fmtDateTime, peso, errorKind, fetchPlans, supportUrl } from './orbitAdmin';
import { PlanBadge, StatusBadge, AttentionChips } from './OrbitAdminOrgsPage';

const cell = { background: HOUSE.canvas, borderRadius: 8, padding: '8px 12px' };

function Section({ title, count, children, testId, right }) {
  return (
    <div style={{ ...card, padding: 16 }} data-testid={testId}>
      <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
        <p className="text-xs uppercase tracking-wide" style={sectionTitle}>
          {title}{count !== undefined && <span style={{ color: HOUSE.faint }}> · {count}</span>}
        </p>
        {right}
      </div>
      {children}
    </div>
  );
}

function Fact({ k, v, color }) {
  return (
    <div style={cell}>
      <p className="text-xs uppercase tracking-wide" style={{ color: HOUSE.dim }}>{k}</p>
      <p className="text-sm font-semibold mt-0.5" style={{ color: color || HOUSE.body, overflowWrap: 'anywhere' }}>{v}</p>
    </div>
  );
}

function Lamp({ on }) {
  return <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 999, background: on ? HOUSE.ok : HOUSE.dim, boxShadow: on ? `0 0 8px ${HOUSE.ok}` : 'none', marginRight: 6 }} />;
}

function limitText(n) { return n === null || n === undefined ? 'Unlimited' : String(n); }
function plusDays(n) { const d = new Date(); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10); }

// ── Set plan modal: the few fields + the plan table beside them (architecture §5) ──
function SetPlanModal({ org, onClose, onSaved, api }) {
  const [plans, setPlans] = useState(null);
  const [plan, setPlan] = useState(org.plan || 'free');
  const [expires, setExpires] = useState(org.plan_expires_at ? org.plan_expires_at.slice(0, 10) : plusDays(30));
  const [rate, setRate] = useState(org.monthly_rate ?? '');
  const [slots, setSlots] = useState(org.extra_location_slots ?? 0);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => { fetchPlans().then(setPlans).catch(() => setPlans({ plans: [] })); }, []);
  const paid = plan === 'premium' || plan === 'enterprise';
  const save = async () => {
    setSaving(true);
    try {
      const body = { plan, note };
      if (paid) { body.expires_at = expires || null; if (rate !== '') body.monthly_rate = Number(rate); }
      if (plan === 'premium') body.extra_location_slots = Number(slots) || 0;
      await api.put(`/orgs/${org.id}/plan`, body);
      toast.success(`${org.name} is now on ${PLAN_CONFIG[plan]?.label || plan}`);
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not set the plan'); }
    finally { setSaving(false); }
  };
  const lab = { fontSize: 11, color: HOUSE.dim, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4, display: 'block' };
  return (
    <Modal title={`Set plan · ${org.name}`} onClose={onClose} width={760} testId="orbit-admin-plan-modal">
      <div className="grid gap-4 md:grid-cols-[1fr_1fr]">
        <div className="grid gap-3">
          <div>
            <label style={lab}>Plan</label>
            <div className="grid grid-cols-2 gap-2">
              {['free', 'premium', 'enterprise', 'founder'].map(k => (
                <button key={k} onClick={() => setPlan(k)} data-testid={`orbit-admin-plan-pick-${k}`}
                  style={{ padding: '10px 12px', borderRadius: 8, textAlign: 'left', cursor: 'pointer', background: plan === k ? `${PLAN_CONFIG[k].color}1A` : HOUSE.canvas,
                    border: `1px solid ${plan === k ? PLAN_CONFIG[k].color : HOUSE.line2}`, color: plan === k ? PLAN_CONFIG[k].color : HOUSE.body, fontWeight: 800, fontSize: 13 }}>
                  {PLAN_CONFIG[k].label}
                  <span className="block text-xs" style={{ color: HOUSE.mute, fontWeight: 500 }}>
                    {k === 'free' ? '₱0 · 1 location · 3 members' : k === 'premium' ? '₱299/mo · 5 locations · 10 seats' : k === 'enterprise' ? '₱1,499/mo · unlimited' : 'the owner · unlimited · never expires'}
                  </span>
                </button>
              ))}
            </div>
          </div>
          {paid && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label style={lab}>Expires (end of that day)</label>
                <input type="date" value={expires} onChange={e => setExpires(e.target.value)} style={inputStyle} data-testid="orbit-admin-plan-expires" />
                <p className="text-xs mt-1" style={{ color: HOUSE.faint }}>Empty = never expires (negotiated)</p>
              </div>
              <div>
                <label style={lab}>Monthly rate (₱)</label>
                <input type="number" min="0" value={rate} onChange={e => setRate(e.target.value)} placeholder={plan === 'premium' ? '299 list' : '1499 list'} style={inputStyle} data-testid="orbit-admin-plan-rate" />
                <p className="text-xs mt-1" style={{ color: HOUSE.faint }}>Empty = the list price</p>
              </div>
            </div>
          )}
          {plan === 'premium' && (
            <div>
              <label style={lab}>Extra location slots (₱499 each, one-time, up to 5)</label>
              <input type="number" min="0" max="5" value={slots} onChange={e => setSlots(e.target.value)} style={inputStyle} data-testid="orbit-admin-plan-slots" />
            </div>
          )}
          <div>
            <label style={lab}>Note (kept with the plan)</label>
            <input value={note} onChange={e => setNote(e.target.value)} placeholder="e.g. paid via GCash ref 1234" style={inputStyle} data-testid="orbit-admin-plan-note" />
          </div>
          <div className="flex gap-2 justify-end">
            <ActionButton onClick={onClose}>Cancel</ActionButton>
            <ActionButton tone="primary" onClick={save} disabled={saving} testId="orbit-admin-plan-save">{saving ? 'Saving…' : 'Set plan'}</ActionButton>
          </div>
        </div>
        <div style={{ ...cell, fontSize: 12 }} data-testid="orbit-admin-plan-table">
          <p className="text-xs uppercase tracking-wide mb-2" style={{ color: HOUSE.dim }}>The plan table (reference)</p>
          {!plans ? <p style={{ color: HOUSE.faint }}>Loading…</p> : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr style={{ color: HOUSE.dim, textAlign: 'left' }}>{['', 'Free', 'Premium', 'Enterprise'].map(h => <th key={h} style={{ padding: '4px 6px', fontWeight: 800 }}>{h}</th>)}</tr></thead>
              <tbody>
                {[
                  ['Monthly', p => p.monthly ? `₱${p.monthly.toLocaleString()}` : '₱0'],
                  ['Yearly', p => p.yearly ? `₱${p.yearly.toLocaleString()}` : '—'],
                  ['Locations', p => p.locations ?? '∞'],
                  ['Members', p => p.members ?? (p.included_members ? `${p.included_members} + ₱${p.extra_member}` : '∞')],
                  ['Printers', p => p.printers ?? '∞'],
                  ['Docs / mo', p => p.documents ?? '∞'],
                  ['History', p => p.history_days ? `${p.history_days} d` : '∞'],
                ].map(([label, f]) => (
                  <tr key={label} style={{ borderTop: `1px solid ${HOUSE.line}` }}>
                    <td style={{ padding: '4px 6px', color: HOUSE.mute }}>{label}</td>
                    {['free', 'premium', 'enterprise'].map(k => { const p = plans.plans.find(x => x.id === k) || {}; return <td key={k} style={{ padding: '4px 6px', color: HOUSE.body }}>{f(p)}</td>; })}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <p className="mt-2" style={{ color: HOUSE.faint }}>Lapse = {plans?.grace_days ?? 3}-day grace, then Free. Nothing is deleted. Extra Premium locations ₱{plans ? 499 : 499} one-time, ceiling 10.</p>
        </div>
      </div>
    </Modal>
  );
}

function SuspendModal({ org, onClose, onDone, api }) {
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const go = async () => {
    if (!reason.trim()) { toast.error('A reason is required — the owner sees it'); return; }
    setSaving(true);
    try { await api.post(`/orgs/${org.id}/suspend`, { reason: reason.trim() }); toast.success(`${org.name} suspended`); onDone(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not suspend'); }
    finally { setSaving(false); }
  };
  return (
    <Modal title={`Suspend · ${org.name}`} onClose={onClose} testId="orbit-admin-suspend-modal">
      <p className="text-sm mb-3" style={{ color: HOUSE.body }}>Printing stops at once for every member. HUBs stay paired and the plan is untouched; Activate restores everything. The reason is shown to the owner.</p>
      <textarea value={reason} onChange={e => setReason(e.target.value)} rows={3} placeholder="e.g. chargeback on the September payment" style={{ ...inputStyle, resize: 'vertical' }} data-testid="orbit-admin-suspend-reason" />
      <div className="flex gap-2 justify-end mt-3">
        <ActionButton onClick={onClose}>Cancel</ActionButton>
        <ActionButton tone="danger" onClick={go} disabled={saving} testId="orbit-admin-suspend-confirm">{saving ? 'Working…' : 'Suspend'}</ActionButton>
      </div>
    </Modal>
  );
}

function ExtendPopover({ org, onClose, onDone, api }) {
  const [days, setDays] = useState('30');
  const [saving, setSaving] = useState(false);
  const go = async (n) => {
    setSaving(true);
    try { const r = await api.post(`/orgs/${org.id}/extend`, { days: n }); toast.success(`Extended to ${fmtDate(r.data.plan_expires_at)}`); onDone(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not extend'); }
    finally { setSaving(false); }
  };
  return (
    <div style={{ ...card, border: `1px solid ${HOUSE.line2}`, padding: 12, position: 'absolute', right: 0, top: '110%', zIndex: 20, width: 260 }} data-testid="orbit-admin-extend-popover" onClick={e => e.stopPropagation()}>
      <p className="text-xs mb-2" style={{ color: HOUSE.mute }}>Adds to the later of today and the current expiry. Ends grace.</p>
      <div className="flex gap-2 mb-2">
        <ActionButton tone="ok" small onClick={() => go(30)} disabled={saving} testId="orbit-admin-extend-30">+30 days</ActionButton>
        <ActionButton tone="ok" small onClick={() => go(365)} disabled={saving} testId="orbit-admin-extend-365">+365 days</ActionButton>
      </div>
      <div className="flex gap-2">
        <input type="number" min="1" value={days} onChange={e => setDays(e.target.value)} style={{ ...inputStyle, width: 90 }} data-testid="orbit-admin-extend-days" />
        <ActionButton small onClick={() => go(Number(days) || 0)} disabled={saving} testId="orbit-admin-extend-custom">Add</ActionButton>
        <ActionButton small onClick={onClose}>Close</ActionButton>
      </div>
    </div>
  );
}

export default function OrbitAdminOrgDetailPage() {
  const ready = useSuperAdminGate();
  const navigate = useNavigate();
  const { orgId } = useParams();
  const api = useMemo(() => orbitAdminApi(), []);
  const [d, setD] = useState(null);
  const [state, setState] = useState('loading');
  const [modal, setModal] = useState(null);            // 'plan' | 'suspend' | 'extend' | null
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => {
    api.get(`/orgs/${orgId}`).then(r => { setD(r.data); setNotes(r.data.admin_notes || ''); setState('ok'); }).catch(e => setState(errorKind(e)));
  }, [api, orgId]);
  useEffect(() => { if (ready) { setState('loading'); load(); } }, [ready, load]);

  const after = () => { setModal(null); load(); };

  const act = async (label, fn) => {
    setBusy(label);
    try { await fn(); load(); } catch (e) { toast.error(e.response?.data?.detail || `Could not ${label}`); }
    finally { setBusy(null); }
  };

  const saveNotes = async () => {
    if ((d?.admin_notes || '') === notes) return;
    try { await api.put(`/orgs/${orgId}/notes`, { admin_notes: notes }); toast.success('Notes saved'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not save notes'); }
  };

  // Support session — a 2-hour owner token; open in a new tab, or copy the hand-off link.
  const openAsOwner = async (open) => {
    setBusy('impersonate');
    try {
      const r = await api.post(`/orgs/${orgId}/impersonate`);
      const url = supportUrl(r.data.token);
      if (open) window.open(url, '_blank', 'noopener');
      else { await navigator.clipboard.writeText(url); toast.success('Support link copied — valid 2 hours, one tab per paste'); }
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not open a support session'); }
    finally { setBusy(null); }
  };

  const title = d?.name || 'Organization';
  const crumbs = [{ label: 'Organizations', to: '/superadmin/orbitprint/orgs' }, { label: title }];
  const plan = d ? (PLAN_CONFIG[d.plan] || {}) : {};
  const inEffect = d ? dateInEffect(d) : null;
  const blocked = d && (d.account_status === 'suspended' || d.account_status === 'deactivated');

  return (
    <PlatformAdminShell product="orbitprint" title={title} shelves={SHELVES} active="orgs" crumbs={crumbs}
      headerRight={
        <button onClick={load} data-testid="orbit-admin-detail-refresh"
          style={{ background: HOUSE.card, border: `1px solid ${HOUSE.line2}`, color: HOUSE.mute, borderRadius: 8, padding: '8px 14px', fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
          <RefreshCw className="h-3.5 w-3.5" /> Refresh
        </button>
      }>
      {!ready ? null : state === 'loading' ? <RouteState kind="loading" /> :
       state === 'notfound' ? <RouteState kind="notfound" message="No organization with that id." /> :
       state === 'denied' ? <RouteState kind="denied" message="Super admin access required." /> :
       state === 'error' ? <RouteState kind="error" message="Could not load this organization." onRetry={load} /> : (
        <div style={{ padding: 16, display: 'grid', gap: 16, maxWidth: 1100 }} onClick={() => modal === 'extend' && setModal(null)}>
          {/* 1. Header — identity + the primary actions */}
          <div style={{ ...card, padding: 16 }} data-testid="orbit-admin-detail-header">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div style={{ minWidth: 0 }}>
                <div className="flex items-center gap-2 flex-wrap">
                  <PlanBadge plan={d.plan} /><StatusBadge status={d.account_status} />
                  <Badge color={HOUSE.mute}>{d.kind}</Badge>
                </div>
                <p className="text-sm mt-2" style={{ color: HOUSE.body }}>
                  Owner <span style={{ color: HOUSE.text, fontWeight: 700 }}>{d.owner?.full_name || '—'}</span> · {d.owner?.email || '—'}
                  {d.owner && d.owner.active === false && <span style={{ color: HOUSE.bad }}> · account inactive</span>}
                </p>
                <p className="text-xs mt-1" style={{ color: HOUSE.mute }}>Registered {fmtDate(d.created_at)} · id <span style={{ fontFamily: 'monospace' }}>{d.id}</span></p>
              </div>
              <div className="text-right">
                <p className="text-xs uppercase tracking-wide" style={{ color: HOUSE.dim }}>In effect</p>
                <p className="text-sm font-semibold" style={{ color: inEffect.color }} data-testid="orbit-admin-detail-in-effect">{inEffect.text}</p>
                {d.attention?.length > 0 && <div className="mt-2 flex justify-end"><AttentionChips flags={d.attention} /></div>}
              </div>
            </div>
            <div className="flex gap-2 flex-wrap mt-4" data-testid="orbit-admin-detail-actions">
              <ActionButton tone="amber" onClick={() => openAsOwner(true)} disabled={busy === 'impersonate' || d.owner?.active === false} testId="orbit-admin-open-as-owner">Open as owner</ActionButton>
              <ActionButton onClick={() => openAsOwner(false)} disabled={busy === 'impersonate' || d.owner?.active === false} testId="orbit-admin-copy-support-link">Copy support link</ActionButton>
              <span style={{ flex: 1 }} />
              {blocked ? (
                <ActionButton tone="ok" onClick={() => act('activate', () => api.post(`/orgs/${orgId}/activate`).then(() => toast.success(`${d.name} is active again`)))} disabled={busy === 'activate'} testId="orbit-admin-activate">Activate</ActionButton>
              ) : (
                <ActionButton tone="danger" onClick={() => setModal('suspend')} testId="orbit-admin-suspend">Suspend</ActionButton>
              )}
            </div>
            {d.account_status === 'suspended' && d.org?.suspend_reason && (
              <p className="text-xs mt-3 rounded-lg px-3 py-2" style={{ background: '#1A0A0E', border: `1px solid ${HOUSE.bad}55`, color: HOUSE.bad }} data-testid="orbit-admin-detail-suspend-reason">
                Suspended {fmtDate(d.org.suspended_at)} · {d.org.suspend_reason}
              </p>
            )}
          </div>

          {/* 2. Plan & money — the money facts and their actions */}
          <Section title="Plan & money" testId="orbit-admin-detail-plan"
            right={
              <div className="flex gap-2 relative" onClick={e => e.stopPropagation()}>
                <ActionButton small onClick={() => setModal('plan')} testId="orbit-admin-set-plan">Set plan</ActionButton>
                {(d.plan === 'premium' || d.plan === 'enterprise') && (
                  <ActionButton small tone="ok" onClick={() => setModal(modal === 'extend' ? null : 'extend')} testId="orbit-admin-extend">Extend</ActionButton>
                )}
                {modal === 'extend' && <ExtendPopover org={d} api={api} onClose={() => setModal(null)} onDone={after} />}
              </div>
            }>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Fact k="Plan" v={plan.label || d.plan} color={plan.color} />
              <Fact k="Monthly" v={d.plan === 'free' || d.plan === 'founder' ? '₱0' : d.monthly_rate != null ? `${peso(d.monthly_rate)} · negotiated` : `${peso(d.monthly_estimate)} · list`} />
              <Fact k={d.account_status === 'grace_period' ? 'Grace until (in effect)' : 'Expires (in effect)'}
                    v={d.account_status === 'grace_period' ? fmtDate(d.grace_until) : d.plan_expires_at ? fmtDate(d.plan_expires_at) : d.plan === 'free' ? '—' : 'Never'} color={inEffect.color} />
              <Fact k="Plan changed" v={d.plan_changed_at ? `${fmtDate(d.plan_changed_at)} · ${d.org?.plan_changed_by || '—'}` : '—'} />
              <Fact k="Locations" v={`${d.counts.locations} of ${limitText(d.location_cap)}${d.extra_location_slots ? ` (incl. ${d.extra_location_slots} bought slot${d.extra_location_slots === 1 ? '' : 's'})` : ''}`}
                    color={d.paused.locations > 0 ? HOUSE.info : undefined} />
              <Fact k="Members" v={`${d.counts.members}${d.extra_members > 0 ? ` · ${d.extra_members} extra seat${d.extra_members === 1 ? '' : 's'} × ₱49` : ''}`} color={d.paused.members > 0 ? HOUSE.info : undefined} />
              <Fact k="Printers" v={String(d.counts.printers)} color={d.paused.printers > 0 ? HOUSE.info : undefined} />
              <Fact k="Documents this month" v={`${d.documents_this_month} cloud · ${d.documents_local_this_month} local`} />
            </div>
            {(d.paused.locations > 0 || d.paused.members > 0 || d.paused.printers > 0) && (
              <p className="text-xs mt-3 rounded-lg px-3 py-2" style={{ background: '#061A1E', border: `1px solid ${HOUSE.info}55`, color: HOUSE.info }} data-testid="orbit-admin-detail-paused">
                Paused by plan: {[d.paused.locations && `${d.paused.locations} location${d.paused.locations === 1 ? '' : 's'}`, d.paused.members && `${d.paused.members} member${d.paused.members === 1 ? '' : 's'}`, d.paused.printers && `${d.paused.printers} printer${d.paused.printers === 1 ? '' : 's'}`].filter(Boolean).join(', ')}. Nothing was deleted — a bigger plan switches them back on.
              </p>
            )}
            {d.previous_plan && d.plan === 'free' && (
              <p className="text-xs mt-2" style={{ color: HOUSE.mute }}>Was {PLAN_CONFIG[d.previous_plan]?.label || d.previous_plan} until {fmtDate(d.downgraded_at)} (lapsed, not cancelled).</p>
            )}
            {d.plan_note && <p className="text-xs mt-2" style={{ color: HOUSE.mute }}>Plan note · <span style={{ color: HOUSE.body }}>{d.plan_note}</span></p>}
            <div className="mt-3">
              <p className="text-xs uppercase tracking-wide mb-1" style={{ color: HOUSE.dim }}>Admin notes · saved when you click away</p>
              <textarea value={notes} onChange={e => setNotes(e.target.value)} onBlur={saveNotes} rows={2} placeholder="Anything the next super admin should know about this organization"
                style={{ ...inputStyle, resize: 'vertical' }} data-testid="orbit-admin-notes" />
            </div>
          </Section>

          {/* 3. Locations & HUBs */}
          <Section title="Locations & HUBs" count={d.locations.length} testId="orbit-admin-detail-locations">
            {d.locations.length === 0 ? <p className="text-sm" style={{ color: HOUSE.faint }}>No HUB paired yet.</p> : (
              <div className="grid gap-2">
                {d.locations.map(l => (
                  <div key={l.id} style={{ ...cell, display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', alignItems: 'center' }} data-testid={`orbit-admin-location-${l.id}`}>
                    <div style={{ minWidth: 0 }}>
                      <p className="text-sm font-semibold" style={{ color: HOUSE.text }}><Lamp on={l.online} />{l.name}
                        {l.status === 'disabled' && <span style={{ marginLeft: 6 }}><Badge color={HOUSE.bad}>disabled</Badge></span>}
                        {l.paused_by_plan && <span style={{ marginLeft: 6 }}><Badge color={HOUSE.info}>paused by plan</Badge></span>}
                        {l.paused_by_user && <span style={{ marginLeft: 6 }}><Badge color={HOUSE.mute}>paused by owner</Badge></span>}
                      </p>
                      <p className="text-xs" style={{ color: HOUSE.mute }}>
                        {l.hostname || 'unknown PC'} · v{l.app_version || '?'} · mode {l.mode} · {l.online ? 'online' : `last seen ${fmtDateTime(l.last_seen)}`} · paired {fmtDate(l.paired_at)}
                      </p>
                    </div>
                    <div className="flex gap-1 flex-wrap justify-end">
                      {l.printers.length === 0 ? <span className="text-xs" style={{ color: HOUSE.faint }}>no printers</span> : l.printers.map(p => (
                        <Badge key={p.id} color={p.paused_by_plan ? HOUSE.info : p.status === 'ready' ? HOUSE.ok : p.status === 'printing' ? HOUSE.info : p.status === 'attention' ? HOUSE.warn : HOUSE.dim}>
                          {p.name}{p.has_preset ? ' · preset' : ''}{p.shared === false ? ' · private' : ''}
                        </Badge>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Section>

          {/* 4. Members */}
          <Section title="Members" count={d.members.length} testId="orbit-admin-detail-members">
            <div className="grid gap-1">
              {d.members.map(m => (
                <div key={m.user_id} style={{ ...cell, display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                  <p className="text-sm" style={{ color: HOUSE.text }}>
                    <span style={{ fontWeight: 700 }}>{m.full_name || m.email}</span> <span style={{ color: HOUSE.mute }}>· {m.email}</span>
                    {m.active === false && <span style={{ color: HOUSE.bad }}> · inactive</span>}
                  </p>
                  <p className="text-xs flex items-center gap-2" style={{ color: HOUSE.mute }}>
                    <Badge color={m.role === 'owner' ? HOUSE.gold : m.role === 'admin' ? HOUSE.info : HOUSE.mute}>{m.role}</Badge>
                    {m.paused_by_plan && <Badge color={HOUSE.info}>paused by plan</Badge>}
                    joined {fmtDate(m.joined_at)}
                  </p>
                </div>
              ))}
            </div>
          </Section>

          {/* 5. Usage */}
          <Section title="Usage · last 6 months" testId="orbit-admin-detail-usage">
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
              {d.usage.map(u => (
                <div key={u.month} style={cell}>
                  <p className="text-xs" style={{ color: HOUSE.dim }}>{u.month}</p>
                  <p style={{ fontFamily: 'Oswald, sans-serif', fontSize: 20, color: HOUSE.text }}>{u.documents}</p>
                  {u.documents_local > 0 && <p className="text-xs" style={{ color: HOUSE.mute }}>+{u.documents_local} local</p>}
                </div>
              ))}
            </div>
          </Section>

          {/* 6. Recent jobs */}
          <Section title="Recent jobs" count={d.jobs.length} testId="orbit-admin-detail-jobs">
            {d.jobs.length === 0 ? <p className="text-sm" style={{ color: HOUSE.faint }}>Nothing printed yet.</p> : (
              <div className="grid gap-1">
                {d.jobs.map(j => (
                  <div key={j.id} style={{ ...cell, display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                    <p className="text-sm" style={{ color: HOUSE.text, minWidth: 0, overflowWrap: 'anywhere' }}>{j.title} <span className="text-xs" style={{ color: HOUSE.mute }}>· {j.printer_name} @ {j.location_name} · {j.created_by_name}{j.copies > 1 ? ` · ×${j.copies}` : ''}</span></p>
                    <p className="text-xs" style={{ color: HOUSE.mute }}>
                      <Badge color={j.status === 'printed' ? HOUSE.ok : j.status === 'failed' ? HOUSE.bad : ['pending', 'claimed'].includes(j.status) ? HOUSE.info : HOUSE.mute}>{j.status}</Badge> {fmtDateTime(j.created_at)}
                      {j.error_message && <span style={{ color: HOUSE.bad }}> · {j.error_message}</span>}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </Section>

          {/* 7. History */}
          <Section title="History" count={d.log.length} testId="orbit-admin-detail-log"
            right={<button onClick={() => navigate(`/superadmin/orbitprint/log?org_id=${d.id}`)} style={{ background: 'none', border: 'none', color: HOUSE.accent, cursor: 'pointer', fontSize: 12, padding: 0 }} data-testid="orbit-admin-detail-log-link">Full log →</button>}>
            {d.log.length === 0 ? <p className="text-sm" style={{ color: HOUSE.faint }}>No admin actions on this organization yet.</p> : (
              <div className="grid gap-1">
                {d.log.map(x => (
                  <div key={x.id} style={{ ...cell, display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                    <p className="text-sm" style={{ color: HOUSE.text }}>{ACTION_LABELS[x.action] || x.action}
                      <span className="text-xs" style={{ color: HOUSE.mute }}> · {x.by}{x.payload?.to ? ` · → ${x.payload.to}` : ''}{x.payload?.days ? ` · +${x.payload.days} days` : ''}{x.payload?.reason ? ` · ${x.payload.reason}` : ''}{x.payload?.note ? ` · ${x.payload.note}` : ''}</span>
                    </p>
                    <p className="text-xs" style={{ color: HOUSE.mute }}>{fmtDateTime(x.at)}</p>
                  </div>
                ))}
              </div>
            )}
          </Section>

          {modal === 'plan' && <SetPlanModal org={d} api={api} onClose={() => setModal(null)} onSaved={after} />}
          {modal === 'suspend' && <SuspendModal org={d} api={api} onClose={() => setModal(null)} onDone={after} />}
        </div>
      )}
    </PlatformAdminShell>
  );
}
